import { useAnalytics } from "@codat/analytics";
import {
  ArrowDownIcon,
  Avatar,
  Button,
  Popover,
  Typography,
} from "@codat/orchard-ui";
import { useQueryClient } from "@tanstack/react-query";
import { ClientType, UserState } from "api/public";
import { useGetBrandingQuery } from "api/queries/useGetBrandingQuery";
import {
  Client,
  useCreateClientSelectLog,
  useGetClientsQuery,
} from "api/users";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { matchPath, useLocation } from "react-router-dom";
import Cookies from "universal-cookie";

import { paths } from "components/App/paths";
import { createNavPermissionMap } from "components/NavContainer/utils";
import config from "config";

import ClientsList from "./ClientsList";
import CurrentClientSummary from "./CurrentClientSummary";
import styles from "./UserClientsDropdown.module.scss";

export function getInstanceType(clientType: ClientType) {
  switch (clientType) {
    case "ClientStartUp":
    case "ClientProd":
    case "ClientFreeTrial":
      return "Production instance";
    case "CodatInternal":
    case "CodatSalesDemo":
    case "CodatQATest":
    case "ClientTestStartup":
    case "ClientTest":
    case "ClientProspect":
      return "Test instance";
    case "Unknown":
    default:
      return "";
  }
}

interface UserClientsDropdownProps {
  userState: UserState;
}

const cookies = new Cookies();

// Wrap window.location.reload so it can be stubbed in tests
export const Location = {
  reload: () => window.location.reload(),
};

const UserClientsDropdown = ({ userState }: UserClientsDropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [{ data: branding, loading }] = useGetBrandingQuery();
  const [{ data: clients }] = useGetClientsQuery();
  const [, sendClientSelectLog] = useCreateClientSelectLog();
  const { sendEvent } = useAnalytics();
  const location = useLocation();
  const queryClient = useQueryClient();

  const popoverContentRef = useRef<HTMLDivElement | null>(null);
  const toggleButtonRef = useRef<HTMLDivElement | null>(null);

  const { clientName, clientType, id, userPermissions, userRole, userId } =
    userState;
  const navPermissionMap = createNavPermissionMap(userPermissions, userRole);
  const isFreeAccount = clientType === "ClientFreeTrial";
  const isStartupAccount = clientType === "ClientStartUp";
  const showUpgradeButton =
    (isFreeAccount || isStartupAccount) &&
    navPermissionMap?.settings.billing.permission;

  const onToggle = useCallback(() => {
    setIsOpen(!isOpen);
    sendEvent(!isOpen ? "Open" : "Close");
  }, [sendEvent, isOpen]);

  const onClientSelect = useCallback(
    async (clientId: string) => {
      cookies.set(config.APP_CLIENT_COOKIE_NAME, clientId, {
        path: "/",
        domain: ".codat.io",
      });
      queryClient.invalidateQueries();
      await sendClientSelectLog({
        data: { clientId, userId },
      });
      const isCompaniesDataPage = matchPath(location.pathname, {
        path: Object.values(paths.companies),
      });
      if (isCompaniesDataPage) {
        // Send user to /companies so they don't land on a 404 page after they switch clients
        window.location.assign(window.location.origin + paths.COMPANIES);
      } else {
        // Refresh page so user remains at same path in new client
        Location.reload();
      }
      sendEvent("Client - Click");
    },
    [sendEvent, sendClientSelectLog, userId, location, queryClient]
  );

  const isNotCurrentClient = useCallback(
    (c: Client) => c.clientId !== id,
    [id]
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node | null;

      if (
        target &&
        !popoverContentRef?.current?.contains(target) &&
        !toggleButtonRef?.current?.contains(target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("click", handleClickOutside, true);
    return () => {
      document.removeEventListener("click", handleClickOutside, true);
    };
  }, [popoverContentRef]);

  return (
    <Popover
      size="fit-content"
      removePadding
      popoverClassName={styles.popover}
      isOpen={isOpen}
      onClose={onToggle}
      closeOnEscape
      placement={["bottom-start"]}
      ref={toggleButtonRef}
      content={
        <div data-testid="user-clients-dropdown" ref={popoverContentRef}>
          <CurrentClientSummary
            clientName={clientName}
            clientType={clientType}
            iconSrc={branding?.icon?.image?.src}
            clientId={id}
            showUpgradeButton={showUpgradeButton}
            userState={userState}
          />
          {clients && clients.length > 1 && (
            <ClientsList
              clients={clients.filter(isNotCurrentClient)}
              onClientSelect={onClientSelect}
            />
          )}
        </div>
      }
    >
      <Button
        variant="secondary"
        color="interface"
        className={styles.button}
        data-testid="user-clients-dropdown-button"
        onClick={onToggle}
      >
        <div className={styles.buttonChildren}>
          <Avatar
            isSkeleton={loading}
            alt={clientName}
            src={branding?.icon?.image?.src}
            color="interface"
          />
          <div>
            <Typography
              data-testid="nav-client-name"
              variant="body_sm"
              as="div"
              className={styles.clientName}
            >
              {clientName}
            </Typography>
            <Typography
              className={styles.clientType}
              data-testid="nav-client-type"
              variant="body_xs"
              as="div"
            >
              {getInstanceType(clientType)}
            </Typography>
          </div>
          <ArrowDownIcon />
        </div>
      </Button>
    </Popover>
  );
};

export default UserClientsDropdown;
